import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatClockStamp(dateStr?: string): string {
  if (!dateStr) return "CLASS2A · ARCHIVE";
  try {
    const d = new Date(dateStr);
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const day = String(d.getDate()).padStart(2, "0");
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `CLASS2A · ${day} ${month} ${year}`;
  } catch {
    return "CLASS2A · ARCHIVE";
  }
}
function getCategoryLabel(post: any): string {
  if (post?.categories && post.categories.length > 0) {
    return String(post.categories[0].name || "").toUpperCase();
  }
  return "CLASSROOM UPDATES";
}
function BlogPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="bg-card border border-border rounded-2xl animate-pulse">
            <div className="p-8 md:p-10 space-y-4">
              <div className="h-3 w-24 bg-muted rounded" />
              <div className="h-6 w-full bg-muted rounded" />
              <div className="h-6 w-3/4 bg-muted rounded" />
              <div className="h-4 w-full bg-muted rounded" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border rounded-lg p-8 text-center">
        <p className="font-mono text-sm uppercase tracking-widest text-muted-foreground">
          Updates are currently unavailable
        </p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-border rounded-lg p-8 text-center">
        <p className="font-mono text-sm uppercase tracking-widest text-muted-foreground">
          No classroom news posted yet
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {posts.slice(0, 3).map((post, i) => <article data-index={i} key={post.id} className="group relative bg-card border border-border rounded-2xl transition-all duration-300 hover:border-ring hover:-translate-y-2 shadow-md">
          <div className="relative p-8 md:p-10">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-lg" style={{
              backgroundColor: "hsl(45 100% 55%)"
            }} aria-hidden="true" />
                <span className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-primary">
                  {getCategoryLabel(post)}
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">
                {formatClockStamp(post.date)}
              </span>
            </div>

            <h3 className="font-default text-xl md:text-2xl font-bold uppercase leading-[1.0] tracking-tight text-foreground mb-4 line-clamp-3">
              {post.title}
            </h3>

            {post.excerpt && <p className="font-sans text-sm leading-relaxed text-muted-foreground mb-6 line-clamp-2" dangerouslySetInnerHTML={{
          __html: post.excerpt.replace(/<[^>]*>/g, "")
        }} />}

            <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.18em] pb-1 border-b-2 transition-colors text-primary border-primary">
              READ MORE
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
        </article>)}
    </div>;
}
export default function ClassroomNews() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1780" id="classroom-news" className="bg-background text-foreground relative py-24 md:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-14 md:mb-20">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-2 h-2 rounded-lg" style={{
              backgroundColor: "hsl(45 100% 55%)"
            }} aria-hidden="true" />
              <span className="font-mono text-xs md:text-sm font-medium uppercase tracking-[0.18em] text-primary">CLASSROOM NEWS</span>
            </div>

            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] font-extrabold uppercase leading-[0.92] tracking-tight text-foreground">
              LATEST FROM
              <br />
              <span className="relative inline-block">
                CLASS 2A
                <span className="absolute left-0 right-0 bottom-1 h-1 -rotate-1 bg-primary opacity-60" aria-hidden="true" />
              </span>
            </h2>
          </div>

          <div className="lg:col-span-4 flex lg:justify-end lg:items-end">
            <Link to="/OurJourneyPage" className="inline-flex items-center gap-2 font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.18em] pb-1.5 border-b-2 transition-colors text-foreground border-primary">
              VIEW ALL UPDATES
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <BlogPostsConsumer />
        </PostsProvider>

        <div className="mt-12 md:mt-16 flex justify-center lg:hidden">
          <Link to="/OurJourneyPage">
            <Button variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs font-bold uppercase tracking-[0.12em] px-7 py-6 rounded-lg border-2 border-foreground">
              VIEW ALL UPDATES
              <ArrowRight className="w-4 h-4 ml-1" strokeWidth={2.5} />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}